import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Loader2, ArrowRight, Lock, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { improvePost, improveScene } from "../lib/improve";

const QUICK_DIRECTIONS = [
  "stronger hook",
  "more emotional",
  "more specific",
  "more relatable",
  "punchier outcome",
  "make it more viral",
];

// Re-usable improvement modal. Two modes:
//   scope === "post"  → improves the whole post (targeted; AI decides which 1-3 fields to change)
//   scope === "scene" → improves a single scene (script + on_screen_text + image_prompt)
//
// User edits are honoured: the caller passes `lockedFields` (e.g. ["hook", "scenes[1]"])
// AND the modal exposes a "Allow improvement of my edits" opt-in that the parent toggles.
export const ImproveDialog = ({
  open,
  onOpenChange,
  apiKey,
  scope = "post", // "post" | "scene"
  post,
  sceneIndex = 0,
  lockedFields = [], // top-level field names + "scenes[i]" markers that are user-edited
  onApply, // (patchOrScene) => void
}) => {
  const [direction, setDirection] = useState("");
  const [allowEditedRegen, setAllowEditedRegen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { summary, patch | scene }

  // Reset state every time the dialog re-opens.
  useEffect(() => {
    if (!open) {
      setDirection("");
      setAllowEditedRegen(false);
      setLoading(false);
      setPreview(null);
    }
  }, [open]);

  if (!post) return null;

  const isSceneMode = scope === "scene";
  const sceneRole = isSceneMode
    ? (post.scenes?.[sceneIndex]?.role || ["HOOK", "PROBLEM", "INSIGHT", "CTA"][sceneIndex] || "Scene")
    : null;

  // The actual locked-fields list passed to the AI takes the user's "allow" toggle into account.
  const effectiveLockedFields = allowEditedRegen ? [] : lockedFields;

  const showLockHint = lockedFields.length > 0;

  const apply = async () => {
    if (!apiKey) {
      toast.error("Connect your OpenAI key first");
      return;
    }
    const dir = direction.trim() || "make it more viral";
    setLoading(true);
    setPreview(null);
    try {
      if (isSceneMode) {
        const r = await improveScene({
          apiKey,
          post,
          sceneIndex,
          direction: dir,
        });
        setPreview({ kind: "scene", ...r });
      } else {
        const r = await improvePost({
          apiKey,
          post,
          direction: dir,
          lockedFields: effectiveLockedFields,
        });
        if (!r.patch || Object.keys(r.patch).length === 0) {
          toast.info("AI decided no changes were needed");
          setPreview({ kind: "noop", summary: r.summary || "" });
        } else {
          setPreview({ kind: "post", ...r });
        }
      }
    } catch (e) {
      toast.error(e.message || "Improvement failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmApply = () => {
    if (!preview) return;
    if (preview.kind === "post") {
      onApply?.({ kind: "post", patch: preview.patch });
      toast.success(preview.summary || "Post improved");
    } else if (preview.kind === "scene") {
      onApply?.({ kind: "scene", sceneIndex: preview.sceneIndex, scene: preview.scene });
      toast.success(preview.summary || "Scene improved");
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl bg-zinc-950 border border-white/10 text-white p-0 overflow-hidden"
        data-testid="improve-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="font-display text-lg font-bold text-white flex items-center gap-2">
            <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 text-violet-400 ring-1 ring-violet-500/30">
              <Wand2 className="h-4 w-4" strokeWidth={2} />
            </div>
            Improve this {isSceneMode ? `scene · ${sceneRole}` : "post"}
          </DialogTitle>
          <p className="text-xs text-zinc-500 pt-1 leading-relaxed">
            Tell the AI what to make better. It applies the improvement while
            preserving the 4-scene viral structure, the locked CTA format, and
            on-screen-text alignment.
          </p>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Free-text direction */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">
              Improvement direction
            </label>
            <Textarea
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder="e.g. stronger hook · more emotional · punchier outcome · target beginners"
              data-testid="improve-direction-input"
              className="min-h-[64px] bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-violet-500 resize-none text-sm"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {QUICK_DIRECTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setDirection(q)}
                  data-testid={`improve-quick-${q.replace(/\s+/g, "-")}`}
                  className="text-[11px] rounded-full border border-white/10 bg-zinc-900 px-2.5 py-1 text-zinc-400 hover:text-white hover:border-violet-500/50 transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Locked-edit toggle (post mode only) */}
          {!isSceneMode && showLockHint && (
            <label
              className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3.5 py-3 cursor-pointer"
              data-testid="improve-allow-edits-toggle"
            >
              <input
                type="checkbox"
                checked={allowEditedRegen}
                onChange={(e) => setAllowEditedRegen(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-500"
              />
              <span className="text-xs text-amber-100 leading-relaxed">
                <span className="inline-flex items-center gap-1 font-semibold">
                  <Lock className="h-3 w-3" strokeWidth={2} />
                  Allow improvement of my manual edits
                </span>
                <br />
                <span className="text-amber-200/70 text-[11px]">
                  By default, fields you've manually edited are excluded from regeneration.
                  Tick this to let the AI rewrite them too.
                </span>
              </span>
            </label>
          )}

          {/* Result preview */}
          {preview && preview.kind === "post" && (
            <div
              className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3"
              data-testid="improve-preview-post"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
                Proposed changes
              </div>
              <p className="text-sm text-violet-100 leading-relaxed">
                {preview.summary}
              </p>
              <div className="grid grid-cols-1 gap-2.5">
                {Object.entries(preview.patch).map(([k, v]) => (
                  <DiffRow
                    key={k}
                    field={k}
                    before={post[k]}
                    after={v}
                  />
                ))}
              </div>
            </div>
          )}

          {preview && preview.kind === "scene" && (
            <div
              className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3"
              data-testid="improve-preview-scene"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
                Proposed scene update · {sceneRole}
              </div>
              <p className="text-sm text-violet-100 leading-relaxed">
                {preview.summary}
              </p>
              <DiffRow
                field="script"
                before={post.scenes?.[sceneIndex]?.script}
                after={preview.scene.script}
              />
              <DiffRow
                field="image_prompt"
                before={post.scenes?.[sceneIndex]?.image_prompt}
                after={preview.scene.image_prompt}
              />
            </div>
          )}

          {preview && preview.kind === "noop" && (
            <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4 text-sm text-zinc-300">
              {preview.summary || "No changes — try a more specific direction."}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 bg-zinc-950 flex items-center justify-between gap-2">
          <button
            onClick={() => onOpenChange(false)}
            type="button"
            className="text-xs text-zinc-400 hover:text-white px-3 h-9"
            data-testid="improve-cancel-button"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {!preview && (
              <Button
                onClick={apply}
                disabled={loading || !apiKey}
                type="button"
                data-testid="improve-apply-button"
                className="h-9 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-500 text-white text-xs font-semibold hover:brightness-110 px-4 disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" strokeWidth={2} /> Thinking…</>
                ) : (
                  <><Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={2.5} /> Generate improvement</>
                )}
              </Button>
            )}
            {preview && (preview.kind === "post" || preview.kind === "scene") && (
              <>
                <Button
                  onClick={apply}
                  disabled={loading}
                  type="button"
                  data-testid="improve-regenerate-button"
                  className="h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold px-3"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
                  Try again
                </Button>
                <Button
                  onClick={confirmApply}
                  type="button"
                  data-testid="improve-confirm-button"
                  className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-4"
                >
                  Apply <ArrowRight className="h-3.5 w-3.5 ml-1.5" strokeWidth={2.5} />
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Compact before/after diff row.
const DiffRow = ({ field, before, after }) => {
  const fmt = (v) => {
    if (v == null) return "—";
    if (Array.isArray(v)) return v.join(" ");
    if (typeof v === "object") return JSON.stringify(v).slice(0, 240);
    return String(v);
  };
  if (field === "scenes" && Array.isArray(after)) {
    return (
      <div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-2">
          scenes (4)
        </div>
        <div className="space-y-1.5">
          {after.map((s, i) => (
            <div key={i} className="text-[11px] leading-snug">
              <span className="text-violet-300 font-bold mr-1.5">{s.role || `S${i + 1}`}</span>
              <span className="text-zinc-300">"{s.script}"</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3 space-y-1">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
        {field}
      </div>
      <div className="text-[11.5px] text-zinc-500 line-through line-clamp-2">
        {fmt(before)}
      </div>
      <div className="text-[12.5px] text-emerald-200 leading-snug">
        {fmt(after)}
      </div>
    </div>
  );
};

export default ImproveDialog;
